import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { api } from '../context/AuthContext';

const fmt = (n) => `₹${parseFloat(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

const S = {
  card: { background: '#fff', borderRadius: '16px', padding: '1.5rem', border: '1px solid #f1f5f9', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  badge: (c) => ({
    display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600',
    ...(c === 'green'  && { background: '#dcfce7', color: '#166534' }),
    ...(c === 'yellow' && { background: '#fef9c3', color: '#854d0e' }),
    ...(c === 'red'    && { background: '#fee2e2', color: '#991b1b' }),
    ...(c === 'gray'   && { background: '#f1f5f9', color: '#475569' }),
    ...(c === 'blue'   && { background: '#dbeafe', color: '#1e40af' }),
  }),
};

const statusColor = { ACTIVE: 'blue', OVERDUE: 'red', PAID: 'green', DEFAULTED: 'gray' };

function getTrust(score) {
  if (score >= 80) return { label: 'Trustworthy',  color: '#16a34a', bg: '#dcfce7', emoji: '🟢', desc: 'Excellent credit standing' };
  if (score >= 50) return { label: 'Moderate Risk', color: '#ca8a04', bg: '#fef9c3', emoji: '🟡', desc: 'Room to improve' };
  if (score >= 20) return { label: 'High Risk',     color: '#dc2626', bg: '#fee2e2', emoji: '🔴', desc: 'Pay on time to recover' };
  return           { label: 'Do Not Lend',    color: '#7c3aed', bg: '#ede9fe', emoji: '⛔', desc: 'Severely impacted' };
}

// ─── LAYOUT ───────────────────────────────────────────────────────────────────
function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    api.get('/notifications').then(r => {
      setUnread(r.data.notifications?.filter(n => !n.is_read).length || 0);
    }).catch(() => {});
  }, []);

  const trust = getTrust(user?.trust_score || 100);

  const navItems = [
    { to: '/customer',               label: '🏠 Overview',   end: true },
    { to: '/customer/loans',         label: '💰 My Loans'             },
    { to: '/customer/score',         label: '⭐ Trust Score'           },
    { to: '/customer/notifications', label: `🔔 Alerts${unread > 0 ? ` (${unread})` : ''}` },
    { to: '/customer/profile',       label: '👤 My Profile'            },
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "'Sora', sans-serif", background: '#f8fafc' }}>
      <div style={{ width: '220px', background: '#0c1a2e', display: 'flex', flexDirection: 'column', padding: '1.5rem 1rem', position: 'fixed', height: '100vh', zIndex: 10 }}>
        <div style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg, #06b6d4, #3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>₹</div>
          <div>
            <div style={{ color: '#fff', fontWeight: '700', fontSize: '16px' }}>Udhari</div>
            <div style={{ color: '#64748b', fontSize: '11px' }}>My Account</div>
          </div>
        </div>

        <div style={{ background: 'rgba(6,182,212,0.1)', borderRadius: '12px', padding: '12px', marginBottom: '1.5rem', border: '1px solid rgba(6,182,212,0.2)' }}>
          <div style={{ color: '#67e8f9', fontWeight: '600', fontSize: '13px' }}>{user?.name}</div>
          <div style={{ color: '#64748b', fontSize: '11px', marginTop: '2px' }}>Borrower</div>
          <div style={{ marginTop: '8px', display: 'inline-block', padding: '3px 10px', background: trust.bg, borderRadius: '20px', fontSize: '11px', fontWeight: '600', color: trust.color }}>
            {trust.emoji} {trust.label}
          </div>
        </div>

        <nav style={{ flex: 1 }}>
          {navItems.map(item => (
            <NavLink key={item.to} to={item.to} end={item.end} style={({ isActive }) => ({
              display: 'block', padding: '10px 12px', borderRadius: '10px',
              color: isActive ? '#67e8f9' : '#94a3b8', textDecoration: 'none',
              fontSize: '13px', fontWeight: '500', marginBottom: '4px',
              background: isActive ? 'rgba(6,182,212,0.15)' : 'transparent',
            })}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <button onClick={() => { logout(); navigate('/login'); }}
          style={{ padding: '8px 16px', borderRadius: '10px', cursor: 'pointer', background: 'rgba(255,255,255,0.08)', color: '#94a3b8', border: '1px solid rgba(255,255,255,0.15)', fontSize: '13px', fontWeight: '600', fontFamily: "'Sora',sans-serif", width: '100%' }}>
          🚪 Logout
        </button>
      </div>
      <div style={{ marginLeft: '220px', flex: 1, padding: '2rem', minHeight: '100vh' }}>{children}</div>
    </div>
  );
}

// ─── OVERVIEW ─────────────────────────────────────────────────────────────────
function Overview() {
  const { user } = useAuth();
  const [loans, setLoans] = useState([]);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    Promise.all([api.get('/customer/loans'), api.get('/customer/profile')])
      .then(([lr, pr]) => { setLoans(lr.data.loans || []); setProfile(pr.data.profile); })
      .catch(() => {});
  }, []);

  const active  = loans.filter(l => l.status === 'ACTIVE');
  const overdue = loans.filter(l => l.status === 'OVERDUE');
  const totalDue = loans.reduce((s, l) => s + parseFloat(l.outstanding_amount || 0), 0);
  const trust = getTrust(profile?.trust_score ?? user?.trust_score ?? 100);
  const score = profile?.trust_score ?? user?.trust_score ?? 100;

  return (
    <div>
      <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a', margin: '0 0 2rem' }}>
        Welcome, {profile?.full_name?.split(' ')[0] || user?.name} 👋
      </h1>

      {/* Trust banner */}
      <div style={{ background: `linear-gradient(135deg,${trust.color}22,${trust.color}11)`, border: `1px solid ${trust.color}44`, borderRadius: '16px', padding: '1.5rem', marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
        <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: trust.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: `3px solid ${trust.color}` }}>
          <div style={{ fontSize: '22px', fontWeight: '800', color: trust.color, lineHeight: 1 }}>{score}</div>
          <div style={{ fontSize: '10px', color: trust.color, fontWeight: '600' }}>/ 150</div>
        </div>
        <div>
          <div style={{ fontSize: '18px', fontWeight: '700', color: trust.color }}>{trust.emoji} {trust.label}</div>
          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>{trust.desc}</div>
          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
            Lender: <strong style={{ color: '#0f172a' }}>{profile?.shop_name}</strong> · {profile?.city}
          </div>
        </div>
      </div>

      {/* Overdue alert */}
      {overdue.length > 0 && (
        <div style={{ background: '#fff5f5', border: '1px solid #fca5a5', borderRadius: '12px', padding: '1rem 1.25rem', marginBottom: '1.5rem', display: 'flex', gap: '12px' }}>
          <span style={{ fontSize: '20px' }}>⚠️</span>
          <div>
            <div style={{ fontWeight: '700', color: '#dc2626', fontSize: '14px' }}>{overdue.length} overdue loan{overdue.length > 1 ? 's' : ''}!</div>
            <div style={{ fontSize: '12px', color: '#991b1b', marginTop: '2px' }}>Contact your lender immediately. Penalty is accumulating.</div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '1rem', marginBottom: '2rem' }}>
        {[
          { label: 'Active Loans',    value: active.length,  icon: '💰', color: '#3b82f6' },
          { label: 'Overdue Loans',   value: overdue.length, icon: '⚠️', color: overdue.length > 0 ? '#dc2626' : '#16a34a' },
          { label: 'Total Outstanding', value: fmt(totalDue), icon: '🕐', color: totalDue > 0 ? '#f97316' : '#16a34a' },
        ].map((s, i) => (
          <div key={i} style={{ background: '#f8fafc', borderRadius: '12px', padding: '1rem 1.25rem', border: '1px solid #e2e8f0', textAlign: 'center' }}>
            <div style={{ fontSize: '28px', marginBottom: '4px' }}>{s.icon}</div>
            <div style={{ fontSize: '22px', fontWeight: '700', color: s.color }}>{s.value}</div>
            <div style={{ fontSize: '12px', color: '#64748b' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Active loans list */}
      <div style={S.card}>
        <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#0f172a', margin: '0 0 1rem' }}>Active Loans</h3>
        {!active.length && !overdue.length ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
            <div style={{ fontSize: '40px', marginBottom: '8px' }}>🎉</div>
            <p>No active loans. You're debt-free!</p>
          </div>
        ) : [...overdue, ...active].map(l => (
          <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', borderRadius: '12px', marginBottom: '8px', background: l.status === 'OVERDUE' ? '#fff5f5' : '#f8fafc', border: `1px solid ${l.status === 'OVERDUE' ? '#fca5a5' : '#e2e8f0'}` }}>
            <div>
              <div style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b' }}>{fmt(l.loan_amount)} loan</div>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                Due: {fmtDate(l.due_date)}
                {parseInt(l.days_overdue) > 0 && <span style={{ color: '#dc2626', fontWeight: '600' }}> · {l.days_overdue} days overdue</span>}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: '700', fontSize: '16px', color: '#dc2626' }}>{fmt(l.outstanding_amount)}</div>
              <span style={S.badge(statusColor[l.status] || 'gray')}>{l.status}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── UPI QR MODAL (proper component — hooks must not be called inside IIFE) ───
function UpiQrModal({ data, onClose }) {
  const [timeLeft, setTimeLeft] = useState(600);
  const [paid, setPaid] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (paid) return;
    const t = setInterval(() => setTimeLeft(p => p <= 1 ? 0 : p - 1), 1000);
    return () => clearInterval(t);
  }, [paid]);

  const mins = String(Math.floor(timeLeft / 60)).padStart(2, '0');
  const secs = String(timeLeft % 60).padStart(2, '0');
  const expired = timeLeft === 0;
  const timerColor = timeLeft <= 60 ? '#dc2626' : timeLeft <= 180 ? '#f97316' : '#16a34a';

  const handlePaid = async () => {
    setSending(true);
    try {
      await api.post('/customer/payment-intimation', {
        loan_id: data.loanId,
        amount: data.amount,
        payment_mode: 'UPI',
        upi_id: data.upiId,
      });
      setPaid(true);
      toast.success('Admin has been notified! ✅');
    } catch {
      toast.error('Failed to notify. Please inform via WhatsApp.');
    }
    setSending(false);
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => !paid && onClose()}>
      <div style={{ background: '#fff', borderRadius: '20px', padding: '2rem', textAlign: 'center', maxWidth: '320px', width: '90%' }} onClick={e => e.stopPropagation()}>
        {!paid ? (
          <>
            <div style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', marginBottom: '4px' }}>Pay via UPI</div>
            <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px' }}>Scan with any UPI app</div>

            {/* Timer */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: expired ? '#fee2e2' : '#f0fdf4', border: `1px solid ${timerColor}33`, borderRadius: '30px', padding: '6px 16px', marginBottom: '14px' }}>
              <span style={{ fontSize: '16px' }}>{expired ? '⏰' : '⏱️'}</span>
              <span style={{ fontSize: '20px', fontWeight: '800', color: timerColor, fontFamily: 'monospace', letterSpacing: '2px' }}>{mins}:{secs}</span>
              <span style={{ fontSize: '11px', color: timerColor, fontWeight: '600' }}>{expired ? 'Expired' : 'remaining'}</span>
            </div>

            {expired ? (
              <div style={{ background: '#fee2e2', borderRadius: '12px', padding: '1rem', marginBottom: '1rem', color: '#dc2626', fontWeight: '600', fontSize: '13px' }}>
                ⏰ Session expired. Please close and try again.
              </div>
            ) : (
              <>
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`upi://pay?pa=${data.upiId}&am=${data.amount}&cu=INR&tn=Udhari+Loan+Repayment`)}`}
                  alt="UPI QR"
                  style={{ width: '200px', height: '200px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '12px' }}
                />
                <div style={{ padding: '10px', background: '#f8fafc', borderRadius: '10px', marginBottom: '12px' }}>
                  <div style={{ fontSize: '24px', fontWeight: '800', color: '#dc2626' }}>{fmt(data.amount)}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>to {data.shopName}</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px', fontFamily: 'monospace' }}>{data.upiId}</div>
                </div>
                <button onClick={handlePaid} disabled={sending}
                  style={{ width: '100%', padding: '12px', borderRadius: '10px', background: sending ? '#86efac' : '#22c55e', color: '#fff', border: 'none', fontWeight: '700', fontSize: '14px', cursor: sending ? 'not-allowed' : 'pointer', fontFamily: "'Sora',sans-serif", marginBottom: '8px' }}>
                  {sending ? '⏳ Notifying...' : '✅ I have Paid!'}
                </button>
                {data.adminPhone && (
                  <a href={`https://wa.me/91${data.adminPhone.replace(/\D/g,'')}?text=${encodeURIComponent(`Namaste! Maine ₹${parseFloat(data.amount).toLocaleString('en-IN')} ka UPI payment kiya hai (UPI ID: ${data.upiId}). Kripya confirm karein. 🙏`)}`}
                    target="_blank" rel="noreferrer"
                    style={{ width: '100%', padding: '10px', borderRadius: '10px', background: '#25d366', color: '#fff', fontWeight: '700', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', textDecoration: 'none', marginBottom: '8px' }}>
                    💬 WhatsApp Lender
                  </a>
                )}
              </>
            )}
            <button onClick={onClose} style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontFamily: "'Sora',sans-serif", fontWeight: '600', color: '#374151' }}>Close</button>
          </>
        ) : (
          <div>
            <div style={{ fontSize: '64px', marginBottom: '12px' }}>🎉</div>
            <div style={{ fontSize: '20px', fontWeight: '800', color: '#16a34a', marginBottom: '8px' }}>Payment Intimation Sent!</div>
            <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '1.5rem' }}>Admin has been notified. They will confirm your payment shortly.</div>
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '1rem', marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '22px', fontWeight: '800', color: '#16a34a' }}>{fmt(data.amount)}</div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>to {data.shopName} via UPI</div>
            </div>
            <button onClick={onClose} style={{ width: '100%', padding: '10px', borderRadius: '10px', background: '#16a34a', color: '#fff', border: 'none', cursor: 'pointer', fontFamily: "'Sora',sans-serif", fontWeight: '700', fontSize: '14px' }}>Done ✓</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MY LOANS ─────────────────────────────────────────────────────────────────
function MyLoans() {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [showQR, setShowQR] = useState(null); // { loanId, amount, upiId }
  const [showBank, setShowBank] = useState(null);
  const [showCheque, setShowCheque] = useState(null);

  useEffect(() => {
    api.get('/customer/loans').then(r => { setLoans(r.data.loans || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const totalActive = loans.filter(l => l.status === 'ACTIVE' || l.status === 'OVERDUE').reduce((s, l) => s + parseFloat(l.outstanding_amount || 0), 0);
  const totalPaid = loans.reduce((s, l) => s + parseFloat(l.amount_paid || 0), 0);

  if (loading) return <div style={{ color: '#94a3b8', padding: '2rem' }}>Loading...</div>;

  return (
    <div>
      <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a', margin: '0 0 1.5rem' }}>My Loans</h1>

      {/* Summary bar */}
      {loans.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
          {[
            { label: 'Total Loans',   value: loans.length,      icon: '📋', color: '#3b82f6' },
            { label: 'Total Paid',    value: fmt(totalPaid),    icon: '✅', color: '#16a34a' },
            { label: 'Outstanding',   value: fmt(totalActive),  icon: '🕐', color: totalActive > 0 ? '#dc2626' : '#16a34a' },
          ].map((s, i) => (
            <div key={i} style={{ background: '#f8fafc', borderRadius: '12px', padding: '1rem', border: '1px solid #e2e8f0', textAlign: 'center' }}>
              <div style={{ fontSize: '22px', marginBottom: '4px' }}>{s.icon}</div>
              <div style={{ fontSize: '18px', fontWeight: '700', color: s.color }}>{s.value}</div>
              <div style={{ fontSize: '11px', color: '#94a3b8' }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {!loans.length ? (
        <div style={{ ...S.card, textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>📋</div>
          <p style={{ color: '#64748b' }}>No loans on record yet.</p>
        </div>
      ) : loans.map(l => (
        <div key={l.id} style={{ ...S.card, marginBottom: '1rem', border: l.status === 'OVERDUE' ? '1px solid #fca5a5' : '1px solid #f1f5f9' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setExpanded(expanded === l.id ? null : l.id)}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                <span style={{ fontWeight: '700', fontSize: '18px', color: '#0f172a' }}>{fmt(l.loan_amount)}</span>
                <span style={S.badge(statusColor[l.status] || 'gray')}>{l.status}</span>
                {parseInt(l.days_overdue) > 0 && <span style={{ fontSize: '11px', color: '#dc2626', fontWeight: '600' }}>⚠️ {l.days_overdue}d overdue</span>}
              </div>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                🏪 {l.shop_name} · 📅 Due {fmtDate(l.due_date)}
              </div>
              {l.purpose && <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>Purpose: {l.purpose}</div>}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '20px', fontWeight: '700', color: parseFloat(l.outstanding_amount) > 0 ? '#dc2626' : '#16a34a' }}>{fmt(l.outstanding_amount)}</div>
              <div style={{ fontSize: '11px', color: '#94a3b8' }}>{l.status === 'PAID' ? '✅ Fully paid' : 'outstanding'}</div>
              <div style={{ fontSize: '16px', marginTop: '4px', color: '#94a3b8' }}>{expanded === l.id ? '▲' : '▼'}</div>
            </div>
          </div>

          {/* Progress bar always visible */}
          <div style={{ marginTop: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>
              <span>Paid: {fmt(l.amount_paid)}</span>
              <span>{Math.round((parseFloat(l.amount_paid) / parseFloat(l.loan_amount)) * 100)}%</span>
              <span>Remaining: {fmt(l.outstanding_amount)}</span>
            </div>
            <div style={{ height: '6px', background: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: '99px', background: l.status === 'PAID' ? '#16a34a' : l.status === 'OVERDUE' ? '#dc2626' : '#3b82f6', width: `${Math.min(100, (parseFloat(l.amount_paid) / parseFloat(l.loan_amount)) * 100)}%`, transition: 'width 0.5s' }} />
            </div>
          </div>

          {/* UPI Pay button — only shown when admin has requested UPI payment */}
          {(l.status === 'ACTIVE' || l.status === 'OVERDUE') && l.upi_id && Number(l.upi_payment_requested) === 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowQR({ loanId: l.id, amount: l.outstanding_amount, upiId: l.upi_id, shopName: l.shop_name, adminPhone: l.admin_phone }); }}
              style={{ marginTop: '12px', width: '100%', padding: '10px', borderRadius: '10px', background: '#22c55e', color: '#fff', border: 'none', fontWeight: '700', fontSize: '13px', cursor: 'pointer', fontFamily: "'Sora',sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
            >
              📱 Pay via UPI — {fmt(l.outstanding_amount)}
            </button>
          )}
          {/* Bank Transfer button */}
          {(l.status === 'ACTIVE' || l.status === 'OVERDUE') && l.bank_account_number && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowBank({ amount: l.outstanding_amount, shopName: l.shop_name, adminPhone: l.admin_phone, bank_account_number: l.bank_account_number, bank_ifsc: l.bank_ifsc, bank_name: l.bank_name, bank_account_holder: l.bank_account_holder }); }}
              style={{ marginTop: '8px', width: '100%', padding: '10px', borderRadius: '10px', background: '#3b82f6', color: '#fff', border: 'none', fontWeight: '700', fontSize: '13px', cursor: 'pointer', fontFamily: "'Sora',sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
            >
              🏦 Pay via Bank Transfer
            </button>
          )}
          {/* Cheque button */}
          {(l.status === 'ACTIVE' || l.status === 'OVERDUE') && l.cheque_payee_name && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowCheque({ amount: l.outstanding_amount, shopName: l.shop_name, adminPhone: l.admin_phone, cheque_payee_name: l.cheque_payee_name, cheque_address: l.cheque_address }); }}
              style={{ marginTop: '8px', width: '100%', padding: '10px', borderRadius: '10px', background: '#8b5cf6', color: '#fff', border: 'none', fontWeight: '700', fontSize: '13px', cursor: 'pointer', fontFamily: "'Sora',sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
            >
              📝 Pay via Cheque
            </button>
          )}

          <button
            onClick={(e) => {
              e.stopPropagation();
              const token = localStorage.getItem('udhari_token');
              const apiBaseC = process.env.REACT_APP_API_URL || 'https://api-udhari.arena19.in/api';
              const tokenC = localStorage.getItem('udhari_token');
              window.open(`${apiBaseC}/loans/${l.id}/receipt?token=${tokenC}`, '_blank');
            }}
            style={{ marginTop: '8px', width: '100%', padding: '10px', borderRadius: '10px', background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', fontWeight: '700', fontSize: '13px', cursor: 'pointer', fontFamily: "'Sora',sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
          >
            🧾 Download Receipt
          </button>

          {/* Expanded details */}
          {expanded === l.id && (
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #f1f5f9' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '1rem' }}>
                {[
                  { label: 'Loan Date',     value: fmtDate(l.date_of_lending || l.created_at) },
                  { label: 'Due Date',      value: fmtDate(l.due_date) },
                  { label: 'Penalty Rate',  value: `${l.penalty_rate}% / ${l.penalty_type?.toLowerCase()}` },
                  { label: 'Penalty',       value: parseFloat(l.current_penalty) > 0 ? <span style={{ color: '#dc2626', fontWeight: '700' }}>{fmt(l.current_penalty)}</span> : '₹0' },
                  { label: 'Lender',        value: l.shop_name },
                  { label: 'Days Overdue',  value: parseInt(l.days_overdue) > 0 ? <span style={{ color: '#dc2626' }}>{l.days_overdue} days</span> : <span style={{ color: '#16a34a' }}>None</span> },
                ].map((d, i) => (
                  <div key={i} style={{ background: '#f8fafc', borderRadius: '8px', padding: '8px 12px' }}>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '2px' }}>{d.label}</div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{d.value}</div>
                  </div>
                ))}
              </div>

              {/* Repayment Schedule */}
              {l.status !== 'PAID' && (
                <div style={{ marginBottom: '1rem', padding: '12px', background: '#eff6ff', borderRadius: '10px', border: '1px solid #bfdbfe' }}>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#1e40af', marginBottom: '8px' }}>📅 Repayment Schedule</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <div style={{ background: '#fff', borderRadius: '8px', padding: '10px 12px' }}>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>Total Loan Amount</div>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b' }}>{fmt(l.loan_amount)}</div>
                    </div>
                    <div style={{ background: '#fff', borderRadius: '8px', padding: '10px 12px' }}>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>Amount Paid So Far</div>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: '#16a34a' }}>{fmt(l.amount_paid)}</div>
                    </div>
                    <div style={{ background: '#fff', borderRadius: '8px', padding: '10px 12px' }}>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>Amount Remaining</div>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: '#dc2626' }}>{fmt(l.outstanding_amount)}</div>
                    </div>
                    <div style={{ background: '#fff', borderRadius: '8px', padding: '10px 12px' }}>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>Payment Due By</div>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: l.status === 'OVERDUE' ? '#dc2626' : '#1e293b' }}>{fmtDate(l.due_date)}</div>
                    </div>
                  </div>
                  {parseFloat(l.current_penalty) > 0 && (
                    <div style={{ marginTop: '8px', padding: '8px 12px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
                      <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: '600' }}>⚠ Penalty added: {fmt(l.current_penalty)} — Total now due: {fmt(parseFloat(l.outstanding_amount) + parseFloat(l.current_penalty))}</span>
                    </div>
                  )}
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#3b82f6' }}>
                    💡 Pay the full remaining amount of <strong>{fmt(l.outstanding_amount)}</strong> by <strong>{fmtDate(l.due_date)}</strong> to avoid penalty and maintain your trust score.
                  </div>
                </div>
              )}

              {/* Repayment history */}
              {l.repayments?.length > 0 && (
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a', marginBottom: '8px' }}>💳 Payment History</div>
                  {l.repayments.map((r, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f0fdf4', borderRadius: '8px', marginBottom: '6px', border: '1px solid #bbf7d0' }}>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#166534' }}>{fmt(r.amount_paid)}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{fmtDate(r.payment_date)} · {r.payment_mode}</div>
                        {r.notes && <div style={{ fontSize: '11px', color: '#94a3b8' }}>{r.notes}</div>}
                      </div>
                      <span style={{ fontSize: '18px' }}>✅</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {/* UPI QR Modal — proper component (fixes hooks-in-IIFE React error) */}
      {showQR && (
        <UpiQrModal data={showQR} onClose={() => setShowQR(null)} />
      )}

      {/* Bank Transfer Modal */}
      {showBank && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowBank(null)}>
          <div style={{ background: '#fff', borderRadius: '20px', padding: '2rem', textAlign: 'center', maxWidth: '340px', width: '90%' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '28px', marginBottom: '8px' }}>🏦</div>
            <div style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', marginBottom: '4px' }}>Bank Transfer</div>
            <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '1.5rem' }}>Transfer to the following account</div>
            <div style={{ background: '#f8fafc', borderRadius: '14px', padding: '1.25rem', textAlign: 'left' }}>
              {[
                { label: 'Account Holder', value: showBank.bank_account_holder },
                { label: 'Account Number', value: showBank.bank_account_number },
                { label: 'IFSC Code', value: showBank.bank_ifsc },
                { label: 'Bank Name', value: showBank.bank_name },
              ].map((item, i) => (
                <div key={i} style={{ marginBottom: i < 3 ? '12px' : '0' }}>
                  <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{item.label}</div>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', fontFamily: 'monospace', marginTop: '2px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {item.value}
                    <button onClick={() => navigator.clipboard.writeText(item.value)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px' }}>📋</button>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: '1rem', padding: '10px', background: '#eff6ff', borderRadius: '10px', border: '1px solid #bfdbfe' }}>
              <div style={{ fontSize: '22px', fontWeight: '800', color: '#1d4ed8' }}>{fmt(showBank.amount)}</div>
              <div style={{ fontSize: '12px', color: '#3b82f6' }}>Amount to transfer to {showBank.shopName}</div>
            </div>
            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '12px' }}>After transfer, inform your lender with UTR number</div>
            {showBank.adminPhone && (
              <a
                href={`https://wa.me/91${showBank.adminPhone.replace(/\D/g,'')}?text=${encodeURIComponent(`Namaste! Maine ₹${parseFloat(showBank.amount).toLocaleString('en-IN')} ka bank transfer kiya hai. Account: ${showBank.bank_account_number}, IFSC: ${showBank.bank_ifsc}. Kripya confirm karein. 🙏`)}`}
                target="_blank"
                rel="noreferrer"
                style={{ marginTop: '10px', width: '100%', padding: '10px', borderRadius: '10px', background: '#25d366', color: '#fff', border: 'none', fontWeight: '700', fontSize: '13px', cursor: 'pointer', fontFamily: "'Sora',sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', textDecoration: 'none' }}
              >
                💬 WhatsApp Lender
              </a>
            )}
            <button onClick={() => setShowBank(null)} style={{ marginTop: '8px', width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontFamily: "'Sora',sans-serif", fontWeight: '600', color: '#374151' }}>Close</button>
          </div>
        </div>
      )}

      {/* Cheque Modal */}
      {showCheque && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowCheque(null)}>
          <div style={{ background: '#fff', borderRadius: '20px', padding: '2rem', textAlign: 'center', maxWidth: '340px', width: '90%' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '28px', marginBottom: '8px' }}>📝</div>
            <div style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', marginBottom: '4px' }}>Pay via Cheque</div>
            <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '1.5rem' }}>Write cheque with these details</div>
            <div style={{ background: '#faf5ff', border: '2px dashed #d8b4fe', borderRadius: '14px', padding: '1.25rem', textAlign: 'left' }}>
              {[
                { label: 'Pay to (Payee Name)', value: showCheque.cheque_payee_name },
                { label: 'Deliver to Address', value: showCheque.cheque_address },
              ].map((item, i) => (
                <div key={i} style={{ marginBottom: i < 1 ? '12px' : '0' }}>
                  <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{item.label}</div>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', marginTop: '2px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                    <span>{item.value}</span>
                    <button onClick={() => navigator.clipboard.writeText(item.value)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', flexShrink: 0 }}>📋</button>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: '1rem', padding: '10px', background: '#f5f3ff', borderRadius: '10px', border: '1px solid #ddd6fe' }}>
              <div style={{ fontSize: '22px', fontWeight: '800', color: '#7c3aed' }}>{fmt(showCheque.amount)}</div>
              <div style={{ fontSize: '12px', color: '#8b5cf6' }}>Write this amount on the cheque</div>
            </div>
            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '12px' }}>Hand over or courier the cheque to your lender</div>
            {showCheque.adminPhone && (
              <a
                href={`https://wa.me/91${showCheque.adminPhone.replace(/\D/g,'')}?text=${encodeURIComponent(`Namaste! Maine ₹${parseFloat(showCheque.amount).toLocaleString('en-IN')} ka cheque "${showCheque.cheque_payee_name}" ke naam se banaya hai. Kripya confirm karein. 🙏`)}`}
                target="_blank"
                rel="noreferrer"
                style={{ marginTop: '10px', width: '100%', padding: '10px', borderRadius: '10px', background: '#25d366', color: '#fff', border: 'none', fontWeight: '700', fontSize: '13px', cursor: 'pointer', fontFamily: "'Sora',sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', textDecoration: 'none' }}
              >
                💬 WhatsApp Lender
              </a>
            )}
            <button onClick={() => setShowCheque(null)} style={{ marginTop: '8px', width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontFamily: "'Sora',sans-serif", fontWeight: '600', color: '#374151' }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── TRUST SCORE ──────────────────────────────────────────────────────────────
function TrustScore() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    Promise.all([api.get('/customer/profile'), api.get('/customer/score-history')])
      .then(([pr, hr]) => { setProfile(pr.data.profile); setHistory(hr.data.history || []); })
      .catch(() => {});
  }, []);

  const score = profile?.trust_score ?? user?.trust_score ?? 100;
  const trust = getTrust(score);
  const pct = Math.min(100, (score / 150) * 100);

  const rules = [
    { action: 'Paid fully on time',        points: '+10', icon: '✅' },
    { action: 'Paid early',                points: '+15', icon: '🚀' },
    { action: 'Partial payment on time',   points: '+5',  icon: '🔄' },
    { action: 'Paid within 7 days late',   points: '-10', icon: '⏰' },
    { action: 'Paid within 15 days late',  points: '-20', icon: '⚠️' },
    { action: 'Paid after 15+ days',       points: '-30', icon: '🔴' },
    { action: 'Marked as defaulted',       points: '-40', icon: '⛔' },
  ];

  return (
    <div>
      <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a', margin: '0 0 1.5rem' }}>My Trust Score</h1>

      {/* Gauge */}
      <div style={{ ...S.card, marginBottom: '1.5rem', textAlign: 'center', padding: '2.5rem 1.5rem' }}>
        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '1rem' }}>Your Current Trust Score</div>
        <div style={{ position: 'relative', display: 'inline-block', marginBottom: '1.5rem' }}>
          <svg width="160" height="90" viewBox="0 0 160 90">
            <path d="M 10 80 A 70 70 0 0 1 150 80" fill="none" stroke="#f1f5f9" strokeWidth="14" strokeLinecap="round" />
            <path d="M 10 80 A 70 70 0 0 1 150 80" fill="none" stroke={trust.color} strokeWidth="14" strokeLinecap="round" strokeDasharray={`${(pct / 100) * 220} 220`} />
          </svg>
          <div style={{ position: 'absolute', bottom: '0', left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }}>
            <div style={{ fontSize: '40px', fontWeight: '800', color: trust.color, lineHeight: 1 }}>{score}</div>
            <div style={{ fontSize: '12px', color: '#94a3b8' }}>out of 150</div>
          </div>
        </div>
        <div style={{ fontSize: '22px', fontWeight: '700', color: trust.color }}>{trust.emoji} {trust.label}</div>
        <div style={{ fontSize: '14px', color: '#64748b', marginTop: '4px' }}>{trust.desc}</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        {/* Rules */}
        <div style={S.card}>
          <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#0f172a', margin: '0 0 1rem' }}>How Score Changes</h3>
          {rules.map((r, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < rules.length - 1 ? '1px solid #f8fafc' : 'none' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ fontSize: '16px' }}>{r.icon}</span>
                <span style={{ fontSize: '12px', color: '#374151' }}>{r.action}</span>
              </div>
              <span style={{ fontWeight: '700', fontSize: '13px', color: r.points.startsWith('+') ? '#16a34a' : '#dc2626' }}>{r.points}</span>
            </div>
          ))}
        </div>

        {/* Score history */}
        <div style={S.card}>
          <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#0f172a', margin: '0 0 1rem' }}>Recent Score History</h3>
          {!history.length ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
              <div style={{ fontSize: '32px' }}>📊</div>
              <p style={{ fontSize: '13px' }}>No history yet.</p>
            </div>
          ) : history.map((h, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < history.length - 1 ? '1px solid #f8fafc' : 'none' }}>
              <div>
                <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>{h.reason || h.action}</div>
                <div style={{ fontSize: '11px', color: '#94a3b8' }}>{fmtDate(h.created_at)} · {h.score_before} → {h.score_after}</div>
              </div>
              <span style={{ fontWeight: '700', fontSize: '14px', color: h.points_change > 0 ? '#16a34a' : '#dc2626' }}>
                {h.points_change > 0 ? '+' : ''}{h.points_change}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Zones */}
      <div style={{ ...S.card, marginTop: '1.5rem' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#0f172a', margin: '0 0 1rem' }}>Score Zones</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px' }}>
          {[
            { range: '80–150', label: 'Trustworthy',  color: '#16a34a', bg: '#dcfce7', emoji: '🟢' },
            { range: '50–79',  label: 'Moderate',     color: '#ca8a04', bg: '#fef9c3', emoji: '🟡' },
            { range: '20–49',  label: 'High Risk',    color: '#dc2626', bg: '#fee2e2', emoji: '🔴' },
            { range: '0–19',   label: 'Do Not Lend',  color: '#7c3aed', bg: '#ede9fe', emoji: '⛔' },
          ].map((z, i) => (
            <div key={i} style={{ background: z.bg, borderRadius: '12px', padding: '12px', textAlign: 'center', border: trust.label === z.label ? `2px solid ${z.color}` : '2px solid transparent' }}>
              <div style={{ fontSize: '22px', marginBottom: '4px' }}>{z.emoji}</div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: z.color }}>{z.label}</div>
              <div style={{ fontSize: '12px', color: z.color, opacity: 0.8 }}>{z.range}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── NOTIFICATIONS ────────────────────────────────────────────────────────────
function Notifications() {
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQR, setShowQR] = useState(null);

  useEffect(() => {
    api.get('/notifications').then(r => { setNotifs(r.data.notifications || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const markRead = async (id) => {
    await api.put(`/notifications/${id}/read`);
    setNotifs(n => n.map(x => x.id === id ? { ...x, is_read: true } : x));
  };

  const markAllRead = async () => {
    await api.put('/notifications/read-all');
    setNotifs(n => n.map(x => ({ ...x, is_read: true })));
  };

  const handleNotifClick = async (n) => {
    if (n.type === 'UPI_PAYMENT_REQUEST' && n.loan_id) {
      if (!n.is_read) markRead(n.id);
      try {
        const r = await api.get('/customer/loans');
        const loans = r.data.loans || [];
        // String comparison to avoid type mismatch
        const loan = loans.find(l => String(l.id) === String(n.loan_id));
        if (loan && loan.upi_id) {
          setShowQR({
            loanId: loan.id,
            amount: loan.outstanding_amount,
            upiId: loan.upi_id,
            shopName: loan.shop_name,
            adminPhone: loan.admin_phone,
          });
        } else if (loans.length > 0) {
          // Fallback — find any active loan with UPI enabled
          const upiLoan = loans.find(l => Number(l.upi_payment_requested) === 1 && l.upi_id);
          if (upiLoan) {
            setShowQR({
              loanId: upiLoan.id,
              amount: upiLoan.outstanding_amount,
              upiId: upiLoan.upi_id,
              shopName: upiLoan.shop_name,
              adminPhone: upiLoan.admin_phone,
            });
          } else {
            toast.error('UPI payment request expired or cancelled.');
          }
        } else {
          toast.error('No active loans found.');
        }
      } catch {
        toast.error('Could not load loan details.');
      }
      return;
    }
    if (!n.is_read) markRead(n.id);
  };

  const icons = { LOAN_CREATED: '💰', PAYMENT_RECORDED: '✅', LOAN_OVERDUE: '⚠️', ACCOUNT_APPROVED: '🎉', ACCOUNT_REJECTED: '❌', UPI_PAYMENT_REQUEST: '📱' };

  if (loading) return <div style={{ color: '#94a3b8', padding: '2rem' }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a', margin: 0 }}>Notifications</h1>
        {notifs.some(n => !n.is_read) && <button onClick={markAllRead} style={{ padding: '8px 16px', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#fff', color: '#374151', fontWeight: '600', cursor: 'pointer', fontSize: '13px', fontFamily: "'Sora',sans-serif" }}>✓ Mark All Read</button>}
      </div>
      {!notifs.length ? (
        <div style={{ ...S.card, textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔔</div>
          <p style={{ color: '#64748b' }}>No notifications yet.</p>
        </div>
      ) : notifs.map(n => {
        const isUpiReq = n.type === 'UPI_PAYMENT_REQUEST';
        return (
          <div key={n.id} onClick={() => handleNotifClick(n)}
            style={{ ...S.card, marginBottom: '10px', background: isUpiReq && !n.is_read ? '#f0fdf4' : n.is_read ? '#fff' : '#eff6ff', border: isUpiReq && !n.is_read ? '1px solid #86efac' : n.is_read ? '1px solid #f1f5f9' : '1px solid #bfdbfe', cursor: 'pointer' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <span style={{ fontSize: '22px' }}>{icons[n.type] || '📢'}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', fontSize: '14px', color: '#0f172a' }}>{n.title}</div>
                <div style={{ fontSize: '13px', color: '#475569', marginTop: '2px' }}>{n.message}</div>
                {isUpiReq && (
                  <div style={{ marginTop: '8px', display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#22c55e', color: '#fff', padding: '5px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '700' }}>
                    📱 Tap to Pay via UPI
                  </div>
                )}
                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>{new Date(n.created_at).toLocaleString('en-IN')}</div>
              </div>
              {!n.is_read && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: isUpiReq ? '#22c55e' : '#3b82f6', flexShrink: 0, marginTop: '6px' }} />}
            </div>
          </div>
        );
      })}

      {showQR && <UpiQrModal data={showQR} onClose={() => setShowQR(null)} />}
    </div>
  );
}

// ─── PROFILE ──────────────────────────────────────────────────────────────────
function Profile() {
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get('/customer/profile').then(r => setProfile(r.data.profile)).catch(() => toast.error('Failed to load'));
  }, []);

  if (!profile) return <div style={{ color: '#94a3b8', padding: '2rem' }}>Loading...</div>;

  const trust = getTrust(profile.trust_score);

  return (
    <div>
      <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a', margin: '0 0 2rem' }}>My Profile</h1>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        {/* Personal Info */}
        <div style={S.card}>
          <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#0f172a', margin: '0 0 1.5rem' }}>👤 Personal Information</h3>
          {[
            { label: 'Full Name',      value: profile.full_name },
            { label: 'Phone',          value: profile.phone },
            { label: 'Age',            value: `${profile.age || '—'} years` },
            { label: 'Current Address', value: profile.current_address },
            { label: 'Pincode',        value: profile.current_pincode },
            { label: 'Occupation',     value: profile.occupation_type },
            { label: 'Member Since',   value: new Date(profile.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) },
          ].map((item, i) => (
            <div key={i} style={{ padding: '10px 0', borderBottom: i < 6 ? '1px solid #f1f5f9' : 'none' }}>
              <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px' }}>{item.label}</div>
              <div style={{ fontSize: '14px', fontWeight: '500', color: '#1e293b' }}>{item.value}</div>
            </div>
          ))}
        </div>

        {/* Documents + Trust */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div style={S.card}>
            <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#0f172a', margin: '0 0 1rem' }}>📄 Documents & GST</h3>
            {[
              { label: 'Aadhaar', value: profile.aadhaar_number ? `${profile.aadhaar_number.slice(0,4)}-XXXX-${profile.aadhaar_number.slice(-4)}` : '—' },
              { label: 'PAN',     value: profile.pan_number || '—' },
              { label: 'GSTIN',   value: profile.gstin || '—', highlight: !!profile.gstin },
            ].map((item, i) => (
              <div key={i} style={{ padding: '10px 0', borderBottom: i < 2 ? '1px solid #f1f5f9' : 'none' }}>
                <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px' }}>{item.label}</div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: item.highlight ? '#16a34a' : '#1e293b', fontFamily: item.highlight ? 'monospace' : 'inherit', background: item.highlight ? '#f0fdf4' : 'transparent', padding: item.highlight ? '3px 8px' : '0', borderRadius: item.highlight ? '6px' : '0', display: 'inline-block' }}>
                  {item.value}
                </div>
              </div>
            ))}
          </div>

          <div style={{ ...S.card, background: `linear-gradient(135deg,${trust.color}11,${trust.color}08)`, border: `1px solid ${trust.color}33` }}>
            <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#0f172a', margin: '0 0 1rem' }}>⭐ Trust Score</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: trust.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: `3px solid ${trust.color}`, flexShrink: 0 }}>
                <div style={{ fontSize: '20px', fontWeight: '800', color: trust.color, lineHeight: 1 }}>{profile.trust_score}</div>
                <div style={{ fontSize: '9px', color: trust.color }}>/ 150</div>
              </div>
              <div>
                <div style={{ fontWeight: '700', color: trust.color, fontSize: '16px' }}>{trust.emoji} {trust.label}</div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>{trust.desc}</div>
              </div>
            </div>
          </div>

          <div style={S.card}>
            <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#0f172a', margin: '0 0 1rem' }}>🏪 My Lender</h3>
            {[
              { label: 'Shop',  value: profile.shop_name },
              { label: 'Owner', value: profile.admin_name },
              { label: 'City',  value: profile.city },
              { label: 'Phone', value: profile.admin_phone },
            ].map((item, i) => (
              <div key={i} style={{ padding: '8px 0', borderBottom: i < 3 ? '1px solid #f1f5f9' : 'none' }}>
                <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '2px' }}>{item.label}</div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── EXPORT ───────────────────────────────────────────────────────────────────
export default function CustomerDashboard() {
  return (
    <Layout>
      <Routes>
        <Route index element={<Overview />} />
        <Route path="loans" element={<MyLoans />} />
        <Route path="score" element={<TrustScore />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="profile" element={<Profile />} />
      </Routes>
    </Layout>
  );
}
